import {Editor} from '@tinymce/tinymce-react'
import {ErrorMessage} from 'formik'
import React, {useRef, useState, useCallback, useEffect} from 'react'
import {handleLabelNameChanges} from '../../automation'
import {processHtmlForA11y, GENERIC_LINK_TEXTS} from '../../../utils/processHtmlForA11y'
import {BLOCKING_TYPES} from '../../../utils/a11yMiddleware'
import {getA11yAiProvider, isAiEnabled} from '../../../utils/ai/aiSuggest'
import {fixTableNoHeaders, fixEmptyHeader, fixTableCaption} from '../../../utils/ai/tableA11yFix'
import {runAxeScan} from '../../../utils/axe/runAxeScan'
import MediaManagerModal from '../MediaManagerModal'

type Props = {
  containerClassName?: string
  label?: any
  limit?: number
  name: string
  initialValue?: any
  rules?: any
  input?: any
  height?: number | undefined
  handleChange: any
  labelClassName?: any
  required?: boolean
  errors?: any
  value?: any
  onA11yError?: (message: string) => void
}

const HEADING_SKIP_CLASS = 'tinymce-a11y-warn-heading'
const LINK_WARN_CLASS = 'tinymce-a11y-warn-link'

function findIssueElement(body: HTMLElement, type: string, context?: string): Element | null {
  const candidates = ((): Element[] => {
    switch (type) {
      case 'empty-alt':
        return Array.from(body.querySelectorAll('img[alt=""], img:not([alt])'))
      case 'image-link-no-alt':
        return Array.from(body.querySelectorAll('a')).filter((a) => {
          const img = a.querySelector('img')
          return !!(img && (!img.getAttribute('alt') || img.getAttribute('alt') === ''))
        })
      case 'empty-link':
        return Array.from(body.querySelectorAll('a')).filter(
          (a) => !a.textContent?.trim() && !a.querySelector('img')
        )
      case 'dead-link':
        return Array.from(body.querySelectorAll('a')).filter((a) => {
          const href = (a.getAttribute('href') ?? '').trim()
          return href === '#' || href === '' || href.toLowerCase().startsWith('javascript:')
        })
      case 'new-tab-no-warning':
        return Array.from(body.querySelectorAll('a[target="_blank"]'))
      case 'heading-skip':
        return Array.from(body.querySelectorAll('.tinymce-a11y-warn-heading'))
      case 'generic-link-text':
        return Array.from(body.querySelectorAll('a')).filter((a) =>
          GENERIC_LINK_TEXTS.has((a.textContent ?? '').trim().toLowerCase())
        )
      case 'table-no-headers':
        return Array.from(body.querySelectorAll('table')).filter(
          (t) => t.querySelectorAll('th').length === 0
        )
      case 'table-empty-header':
        return Array.from(body.querySelectorAll('th')).filter((th) => !th.textContent?.trim())
      case 'table-no-caption':
        return Array.from(body.querySelectorAll('table')).filter((t) => !t.querySelector('caption'))
      case 'table-merged-no-headers':
        return Array.from(
          body.querySelectorAll('td[colspan], td[rowspan], th[colspan], th[rowspan]')
        ).filter((c) => !c.hasAttribute('headers'))
      case 'table-nested':
        return Array.from(body.querySelectorAll('td table, th table'))
      default:
        return []
    }
  })()
  if (!context || candidates.length <= 1) return candidates[0] ?? null
  const match = candidates.find((el) => el.outerHTML.startsWith(context.slice(0, 40)))
  return match ?? candidates[0] ?? null
}

const FormTinyMce: React.FC<Props> = ({
  containerClassName,
  limit,
  labelClassName,
  label,
  name,
  initialValue,
  rules,
  input,
  height,
  handleChange,
  required,
  errors,
  value,
  onA11yError,
  ...rest
}) => {
  const editorRef = useRef<any>(null)
  const [mediaManagerModalOpen, setMediaManagerModalOpen] = useState(false)
  const [editorCallBack, setEditorCallBack] = useState<any>()
  const [blockingError, setBlockingError] = useState<string | null>(null)
  const [aiBusy, setAiBusy] = useState(false)
  const [axeScanning, setAxeScanning] = useState(false)
  const [axeError, setAxeError] = useState(false)

  const handleCloseMediaManagerModal = () => setMediaManagerModalOpen(false)

  const callingFunction = (cb: any) => {
    const callBackFile = {
      callback: (downloadLink: string, title: string) => {
        cb(downloadLink, {title})
      },
    }
    setEditorCallBack(callBackFile)
  }

  const validateA11y = useCallback((): string | null => {
    const editor = editorRef.current
    if (!editor) return null

    const body = editor.getBody() as HTMLElement

    const badImages = Array.from(body.querySelectorAll('img')).filter(
      (img) => !img.getAttribute('alt') || img.getAttribute('alt') === ''
    )
    if (badImages.length > 0) {
      const msg = `Image needs alt text — double-click the image in the editor to add it (${
        badImages.length
      } image${badImages.length > 1 ? 's' : ''} affected)`
      setBlockingError(msg)
      onA11yError?.(msg)
      return msg
    }

    const emptyLinks = Array.from(body.querySelectorAll('a')).filter(
      (a) => !a.textContent?.trim() && !a.querySelector('img')
    )
    if (emptyLinks.length > 0) {
      const msg = 'A link has no text — add descriptive text to all links'
      setBlockingError(msg)
      onA11yError?.(msg)
      return msg
    }

    const headings = Array.from(body.querySelectorAll('h1,h2,h3,h4,h5,h6'))
    for (let i = 1; i < headings.length; i++) {
      const prev = parseInt(headings[i - 1].tagName[1], 10)
      const curr = parseInt(headings[i].tagName[1], 10)
      if (curr - prev > 1) {
        const msg = `Heading skips from H${prev} to H${curr} — use H${
          prev + 1
        } instead (highlighted in amber)`
        setBlockingError(msg)
        onA11yError?.(msg)
        return msg
      }
    }

    setBlockingError(null)
    return null
  }, [onA11yError])

  // Live re-validation: re-scan the current editor content and refresh the panel
  // (badge + lists) in place so fixed issues drop off. Does NOT force the panel open.
  const revalidateA11y = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    const {warnings} = processHtmlForA11y(editor.getContent())
    const violations = warnings.filter((w) => BLOCKING_TYPES.has(w.type as any))
    const nonBlocking = warnings.filter((w) => !BLOCKING_TYPES.has(w.type as any))
    window.dispatchEvent(
      new CustomEvent('a11y/revalidate', {detail: {violations, warnings: nonBlocking}})
    )
  }, [])

  const revalidateTimer = useRef<any>(null)
  const scheduleRevalidate = useCallback(() => {
    if (revalidateTimer.current) clearTimeout(revalidateTimer.current)
    revalidateTimer.current = setTimeout(() => revalidateA11y(), 400)
  }, [revalidateA11y])

  useEffect(() => {
    const onApplyFixes = () => {
      const editor = editorRef.current
      if (!editor) return
      const fixed = processHtmlForA11y(editor.getContent(), {applyAllFixes: true})
      editor.setContent(fixed.html)
      handleChange({target: {name, value: fixed.html}})
      revalidateA11y()
    }
    window.addEventListener('a11y/applyFixes', onApplyFixes)
    return () => window.removeEventListener('a11y/applyFixes', onApplyFixes)
  }, [handleChange, name, revalidateA11y])

  useEffect(() => {
    const onHighlight = (e: Event) => {
      const editor = editorRef.current
      if (!editor) return
      const {type, context} = (e as CustomEvent).detail as {type: string; context?: string}
      const body = editor.getBody() as HTMLElement
      const el = findIssueElement(body, type, context)
      if (el) {
        editor.selection.select(el)
        editor.focus()
        el.scrollIntoView({behavior: 'smooth', block: 'center'})
      }
    }
    window.addEventListener('a11y/highlightIssue', onHighlight)
    return () => window.removeEventListener('a11y/highlightIssue', onHighlight)
  }, [])

  useEffect(() => {
    const onAiSuggest = async (e: Event) => {
      const editor = editorRef.current
      if (!editor) return
      const {type, context} = (e as CustomEvent).detail as {type: string; context?: string}
      const provider = getA11yAiProvider()
      const body = editor.getBody() as HTMLElement

      // Advisory-only: surface a generic, non-blocking notice on any AI failure or
      // empty response. Content is left untouched so the editor can fix it manually.
      const notifyAiError = () => {
        try {
          editor.notificationManager?.open({
            text: 'AI could not generate a suggestion right now. Please add the text manually.',
            type: 'error',
            timeout: 5000,
          })
        } catch {
          /* notificationManager may be unavailable in some editor contexts */
        }
      }

      const el = findIssueElement(body, type, context)
      if (!el) {
        notifyAiError()
        window.dispatchEvent(new CustomEvent('a11y/aiSuggestDone', {detail: {type, context}}))
        return
      }

      const ctx = {pageTitle: document.title}
      setAiBusy(true)
      try {
        let changed = false
        if (type === 'empty-alt' || type === 'image-link-no-alt') {
          const img = (
            el.tagName === 'IMG' ? el : el.querySelector('img')
          ) as HTMLImageElement | null
          if (!img) return
          const suggestion = await provider.suggestAltText(img.getAttribute('src') || '', ctx)
          if (!suggestion.text.trim()) {
            notifyAiError()
            return
          }
          img.setAttribute('alt', suggestion.text)
          editor.selection.select(img)
          // Open TinyMCE's native image dialog so the editor reviews/edits the pre-filled alt.
          editor.execCommand('mceImage')
          changed = true
        } else if (type === 'generic-link-text' || type === 'empty-link') {
          const a = (el.tagName === 'A' ? el : el.closest('a')) as HTMLAnchorElement | null
          if (!a) return
          const suggestion = await provider.suggestLinkText(
            a.getAttribute('href') || '',
            a.textContent || '',
            ctx
          )
          if (!suggestion.text.trim()) {
            notifyAiError()
            return
          }
          editor.undoManager.transact(() => {
            a.textContent = suggestion.text
          })
          editor.selection.select(a)
          editor.nodeChanged()
          editor.focus()
          changed = true
        } else if (type === 'table-no-headers') {
          const table = (
            el.tagName === 'TABLE' ? el : el.closest('table')
          ) as HTMLTableElement | null
          if (!table) return
          changed = await fixTableNoHeaders(table, provider, ctx)
          if (!changed) {
            notifyAiError()
            return
          }
          editor.undoManager.add()
          editor.nodeChanged()
        } else if (type === 'table-empty-header') {
          changed = await fixEmptyHeader(el, provider, ctx)
          if (!changed) {
            notifyAiError()
            return
          }
          editor.undoManager.add()
          editor.nodeChanged()
        } else if (type === 'table-no-caption') {
          const table = (
            el.tagName === 'TABLE' ? el : el.closest('table')
          ) as HTMLTableElement | null
          if (!table) return
          changed = await fixTableCaption(table, provider, ctx)
          if (!changed) {
            notifyAiError()
            return
          }
          editor.undoManager.add()
          editor.nodeChanged()
        }

        if (changed) {
          // Keep the form value in sync (direct DOM edits don't always fire change)
          // and re-validate so the resolved issue drops off the panel immediately.
          handleChange({target: {name, value: editor.getContent()}})
          revalidateA11y()
        }
      } catch {
        notifyAiError()
      } finally {
        setAiBusy(false)
        window.dispatchEvent(new CustomEvent('a11y/aiSuggestDone', {detail: {type, context}}))
      }
    }
    if (isAiEnabled()) {
      window.addEventListener('a11y/aiSuggest', onAiSuggest)
      return () => window.removeEventListener('a11y/aiSuggest', onAiSuggest)
    }
  }, [handleChange, name, revalidateA11y])

  // Reset axe scan state when a new form/modal session starts (initialValue changes = new content)
  useEffect(() => {
    setAxeScanning(false)
    setAxeError(false)
  }, [initialValue])

  // Close the A11yWarningPanel when this editor unmounts (modal closes)
  useEffect(() => {
    return () => {
      window.dispatchEvent(new CustomEvent('a11y/editorClosed'))
    }
  }, [])

  const applyInlineWarnings = useCallback((editor: any) => {
    const body = editor.getBody() as HTMLElement
    if (!body) return

    body.querySelectorAll(`.${HEADING_SKIP_CLASS}, .${LINK_WARN_CLASS}`).forEach((el) => {
      el.classList.remove(HEADING_SKIP_CLASS, LINK_WARN_CLASS)
      el.removeAttribute('title')
    })

    const headings = Array.from(body.querySelectorAll('h1,h2,h3,h4,h5,h6'))
    for (let i = 1; i < headings.length; i++) {
      const prev = parseInt(headings[i - 1].tagName[1], 10)
      const curr = parseInt(headings[i].tagName[1], 10)
      if (curr - prev > 1) {
        headings[i].classList.add(HEADING_SKIP_CLASS)
        headings[i].setAttribute(
          'title',
          `Heading jumps from H${prev} to H${curr} — consider H${prev + 1}`
        )
      }
    }

    body.querySelectorAll('a').forEach((el) => {
      const text = el.textContent?.trim().toLowerCase() ?? ''
      if (GENERIC_LINK_TEXTS.has(text)) {
        el.classList.add(LINK_WARN_CLASS)
        el.setAttribute(
          'title',
          'Describe where this link goes — avoid "click here" or "read more"'
        )
      }
    })
  }, [])

  return (
    <div
      className={label ? 'mb-6' : 'col-lg-6 '}
      data-cy={`${handleLabelNameChanges(name)}-tiny-editor`}
    >
      {label && (
        <label className={`mb-1 fw-bolder fs-6 ${labelClassName || 'col-lg-4'}`}>
          <span className={required ? 'required' : ''}>{label}</span>
        </label>
      )}
      <Editor
        onInit={(evt, editor) => {
          editorRef.current = editor
          ;(editor as any).validateA11y = validateA11y
        }}
        initialValue={initialValue}
        value={value}
        tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
        onEditorChange={(e) => {
          handleChange({target: {name, value: e}})
          scheduleRevalidate()
        }}
        data-cy={`${name}-editor-field`}
        init={{
          relative_urls: false,
          remove_script_host: false,
          convert_urls: true,
          height: height ?? 350,
          menubar: false,
          toolbar_mode: 'sliding',
          forced_root_block: 'p',
          image_description: true,
          image_advtab: true,
          plugins:
            'lists advlist anchor autolink link charmap code directionality emoticons image ' +
            'insertdatetime media nonbreaking pagebreak preview searchreplace ' +
            'visualblocks fullscreen help wordcount table ' +
            'collapse',
          toolbar1:
            'bold italic backcolor | alignleft aligncenter alignright alignjustify | ' +
            'styles undo redo | formatselect | blocks fontfamily fontsize | ' +
            'bullist numlist outdent indent | removeformat | help | ' +
            'image media | table | insertfile | code | ltr rtl | emoticons | insertdatetime | collapse | a11ycheck',
          external_plugins: {
            collapse: '/tinymce/plugins/collapse/plugin.min.js',
          },
          content_style: `
            body { font-family: Helvetica, Arial, sans-serif; font-size: 14px }
            .${HEADING_SKIP_CLASS} { outline: 2px solid #f59e0b; outline-offset: 2px; }
            .${LINK_WARN_CLASS} { text-decoration: underline wavy #f97316; }
            .collapsible { border: 1px solid #ccc; padding: 10px; margin: 5px 0; transition: max-height 0.2s ease-out; }
            .collapsible.collapsed { max-height: 20px; overflow: hidden; background-color: #f9f9f9; }
          `,
          invalid_elements: 'b,i,font,u,strike,tt,center,big,small',
          automatic_uploads: true,
          file_picker_types: 'image',
          file_picker_callback: (_cb: any, _value: any, _meta: any) => {
            setMediaManagerModalOpen(true)
            callingFunction(_cb)
          },
          paste_preprocess: (_plugin: any, args: any) => {
            if (args.content) {
              args.content = processHtmlForA11y(args.content).html
            }
          },
          setup: (editor: any) => {
            editor.on('NodeChange', () => {
              applyInlineWarnings(editor)
            })
            editor.ui.registry.addButton('a11ycheck', {
              text: 'Check accessibility',
              tooltip: 'Scan this content for accessibility issues',
              onAction: async () => {
                const body = editor.getBody() as HTMLElement
                setAxeError(false)
                setAxeScanning(true)
                try {
                  const [axeIssues, {warnings: pipelineWarnings}] = await Promise.all([
                    runAxeScan(body),
                    Promise.resolve(processHtmlForA11y(editor.getContent())),
                  ])
                  const pipelineIssues = pipelineWarnings.map(w => ({
                    type: w.type,
                    message: w.message,
                    context: w.context,
                    impact: BLOCKING_TYPES.has(w.type as any) ? ('critical' as const) : ('moderate' as const),
                  }))
                  window.dispatchEvent(new CustomEvent('a11y/scanResults', {detail: [...pipelineIssues, ...axeIssues]}))
                } catch {
                  // Advisory only: scan failure leaves content + save flow untouched.
                  // Do NOT dispatch an empty scan result here — that would falsely read as "no issues".
                  setAxeError(true)
                } finally {
                  setAxeScanning(false)
                }
              },
            })
          },
        }}
        textareaName={name}
        apiKey='3qyej0twovgwvklxo176yhnorvfpy2f965i3ac4d56gm4fpr'
        {...rest}
      />

      {blockingError && (
        <div role='alert' className='fv-plugins-message-container text-danger fw-bolder small mt-1'>
          ⚠️ {blockingError}
        </div>
      )}

      {aiBusy && (
        <div
          className='fv-plugins-message-container fw-bolder small mt-1'
          style={{color: '#7c3aed'}}
        >
          ✨ Generating suggestion…
        </div>
      )}
      {axeScanning && (
        <div
          className='fv-plugins-message-container fw-bolder small mt-1'
          style={{color: '#1e40af'}}
        >
          🔍 Scanning for accessibility issues…
        </div>
      )}
      {axeError && (
        <div
          className='fv-plugins-message-container fw-bolder small mt-1'
          style={{color: '#b45309'}}
        >
          ⚠️ Accessibility scan unavailable — please try again.
        </div>
      )}

      <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
        <ErrorMessage name={name} component='div' className='field-error-message' />
      </div>

      <MediaManagerModal
        isOpen={mediaManagerModalOpen}
        handleClose={handleCloseMediaManagerModal}
        editorCallBack={editorCallBack}
      />
    </div>
  )
}

export default FormTinyMce
